import {useLayoutEffect, useState} from "react";
import {toast} from "react-toastify";
import {CreateSupplierEmployee} from "../../../../../reduxToolkit/Providers/providersApi";
import {getPhotoPicker} from '../../../../../components/PhotoPicker/SubComponent/Models/Models';
import {errorMsg, validateMinLengthTrimmed} from '../../../../../Apis/functions';
import {
    SUPPLIER_EMPLOYEE_DATA,
    TABS_SUPPLIER_EMPLOYEE
} from '../../../../Dashboard/Company/Employees/Model/Models';
/**
 * @file SupplierFlowEmployeeCreateApp.jsx
 * @description Custom hook for managing the creation flow of a supplier employee user.
 *
 * @Email: azapata@ibl.mx
 * @Author: Ferro Zapata Arnulfo
 * @JobTitle: Full Stack Developer
 */

/**
 * Custom hook to handle the form logic and data flow for creating a supplier employee.
 *
 * @param {function} t - Translation function from i18next.
 * @param {function} dispatch - Redux dispatch function.
 * @param {function} navigate - Function to navigate programmatically.
 * @param {function} setEmptyFields - Setter for emptyFields.
 * @param {boolean} isValidEmail - Flag for valid email input.
 * @param {boolean} isValidPhoneNumber - Flag for valid phone number input.
 * @param {boolean} isValidDob - Flag for valid date of birth input.
 *
 * @returns {Object} Hook utilities and state for managing supplier employee creation.
 */
export const SupplierFlowEmployeeCreateApp = (
    {
        t,
        permission,
        dispatch,
        setEmptyFields,
        isValidEmail,
        isValidPhoneNumber,
        isValidDob,
        supplierId,
        photoPicker,
        setSecretModal,
        setSecretKey,
        setGeneratedPassword,
        passwordGenerator,
        validateEmail,
        validatePhone,
        validatePassword,
        isValidPassword
    }
) => {
    const [user, setUser] = useState({...SUPPLIER_EMPLOYEE_DATA, supplierId : supplierId });

    const [steps, setSteps] = useState([]);
    const [activeStep, setActiveStep] = useState(TABS_SUPPLIER_EMPLOYEE.DETAILS.INDEX);


    useLayoutEffect(() => {
        setSteps( [t('details'),t('add_photo'),t('summary')] )
    }, [t, permission]);

    /**
     * Resets the form fields to their initial state.
     */
    const resetForm = () => {
        setUser({...SUPPLIER_EMPLOYEE_DATA, supplierId : supplierId });
    };

    /**
     * Validates the form fields and triggers the supplier creation if valid.
     * Shows a warning toast if required fields are missing or invalid.
     */
    const FormHandler = () => {
        const hasInvalidField = !isValidDob || !isValidPhoneNumber || !isValidEmail || !isValidPassword;
        if (!user.isValidEmployee() || hasInvalidField) {
            setEmptyFields(true);
            return;
        }
        setEmptyFields(false);
        createSupplier();
    };

    const handleValidateSteps = (step) => {
        switch (step) {
            case TABS_SUPPLIER_EMPLOYEE.DETAILS.INDEX: {
                const hasInvalidField = !isValidDob || !validateEmail(user.email) || !validatePhone(user.phoneNumber) || !validatePassword(user.password);
                if (!user.isValidEmployee() || hasInvalidField) {
                    setEmptyFields(true);
                    return false;
                }
                return true;
            }
            default:
                return true;
        }
    }


    /**
     * Dispatches the action to create a supplier employee using form data and image file.
     * Displays success or error feedback using toast notifications.
     */
    const createSupplier = () => {
        let imageFile = getPhotoPicker(photoPicker);
        const password = user?.password?.trim() !== "" ? user?.password: passwordGenerator()
        dispatch(CreateSupplierEmployee({ data: user.toRegister(password), file: imageFile }))
            .then(({payload:{data:{data, success, code}}}) => {
                if (success) {
                    toast.success(t('has_been_created_successfully'))
                    setEmptyFields(false);
                    setSecretModal(true);
                    setSecretKey(data?.secret);
                    setGeneratedPassword(user?.password?.trim() !== "" ? null: password);
                }else{
                    errorMsg(code)
                    setEmptyFields(true);
                }
            },
        );
    }

    const handleStepChange = (step) => {
        setActiveStep(step);
    };

    return {
        user,
        setUser,
        FormHandler,
        resetForm,
        steps,
        activeStep,
        setActiveStep,
        handleStepChange,
        handleValidateSteps
    };
}
